import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Eye, EyeOff, Save, X, LogOut, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogAdmin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Auth states - null = checking, true = authenticated, false = not authenticated
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(null);
  
  // Blog admin states
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  const hasProcessedSession = useRef(false);
  
  const emptyPost = {
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author: 'Jim Bukowski',
    tags: [],
    featured_image: '',
    published: false
  };

  const [formData, setFormData] = useState(emptyPost);
  const [tagInput, setTagInput] = useState('');

  // Process session_id from URL hash (from Google OAuth redirect)
  useEffect(() => {
    const processSession = async () => {
      // Check if URL has session_id
      if (location.hash?.includes('session_id=')) {
        if (hasProcessedSession.current) return;
        hasProcessedSession.current = true;
        
        const sessionId = location.hash.split('session_id=')[1]?.split('&')[0];
        
        if (sessionId) {
          try {
            const response = await axios.post(
              `${BACKEND_URL}/api/blog-auth/session`,
              { session_id: sessionId },
              { withCredentials: true }
            );
            
            if (response.data.success) {
              setUser(response.data.user);
              setIsAuthenticated(true);
              // Clear the hash from URL
              window.history.replaceState(null, '', '/blog-admin');
              fetchPosts();
              return;
            }
          } catch (error) {
            console.error('Session exchange error:', error);
            setAuthError(error.response?.data?.detail || 'Authentication failed');
            setIsAuthenticated(false);
            window.history.replaceState(null, '', '/blog-admin');
            return;
          }
        }
      }
      
      // No session_id in URL, check existing session
      checkAuth();
    };
    
    processSession();
  }, [location.hash]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/blog-auth/me`,
        { withCredentials: true }
      );
      
      setUser(response.data);
      setIsAuthenticated(true);
      fetchPosts();
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/blog-admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/blog-auth/logout`,
        {},
        { withCredentials: true }
      );
      setUser(null);
      setIsAuthenticated(false);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/blog/posts?published_only=false`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title)
    });
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPost) {
        await axios.put(`${BACKEND_URL}/api/blog/posts/${editingPost.id}`, formData);
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/blog/posts`, formData);
        toast.success('Post created successfully');
      }
      
      setEditingPost(null);
      setIsCreating(false);
      setFormData(emptyPost);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      tags: post.tags || [],
      featured_image: post.featured_image || '',
      published: post.published
    });
    setIsCreating(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/blog/posts/${postId}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleCancel = () => {
    setEditingPost(null);
    setIsCreating(false);
    setFormData(emptyPost);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="blog-admin">
        <div className="admin-login-page">
          <div className="login-card">
            <div className="loading-auth">Checking authentication...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="blog-admin">
        <div className="admin-login-page">
          <div className="login-card">
            <h1>Blog Admin</h1>
            <p>Sign in with your Google account to manage blog posts.</p>
            
            {authError && (
              <div className="auth-error">
                {authError}
              </div>
            )}
            
            <button onClick={handleLogin} className="btn-google-login" data-testid="google-login-btn">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>
            
            <Link to="/" className="back-home-link">
              <ArrowLeft size={16} />
              Back to site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show admin panel if authenticated
  return (
    <div className="blog-admin">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Site</span>
          </Link>
          <h1>Blog Admin</h1>
          <div className="admin-user-info">
            {user && (
              <>
                <span className="user-email">
                  <User size={16} />
                  {user.email}
                </span>
                <button onClick={handleLogout} className="btn-logout" data-testid="logout-btn">
                  <LogOut size={16} />
                  Logout
                </button>
              </>
            )}
            {!isCreating && (
              <button 
                className="btn-create"
                onClick={() => setIsCreating(true)}
                data-testid="new-post-btn"
              >
                <Plus size={18} />
                New Post
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="admin-main">
        {isCreating ? (
          /* Editor Form */
          <div className="post-editor">
            <div className="editor-header">
              <h2>{editingPost ? 'Edit Post' : 'Create New Post'}</h2>
              <button className="btn-cancel" onClick={handleCancel}>
                <X size={18} />
                Cancel
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="editor-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    placeholder="Post title"
                    required
                    data-testid="post-title-input"
                  />
                </div>
                <div className="form-group">
                  <label>Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    placeholder="url-friendly-slug"
                    required
                    data-testid="post-slug-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Author</label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                    placeholder="Author name"
                  />
                </div>
                <div className="form-group">
                  <label>Featured Image URL</label>
                  <input
                    type="url"
                    value={formData.featured_image}
                    onChange={(e) => setFormData({...formData, featured_image: e.target.value})}
                    placeholder="https://example.com/image.jpg or /local-image.jpg"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Excerpt</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  placeholder="Brief summary of the post..."
                  rows={2}
                  required
                  data-testid="post-excerpt-input"
                />
              </div>

              <div className="form-group">
                <label>Content (HTML supported)</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="<p>Your post content here...</p>"
                  rows={15}
                  required
                  data-testid="post-content-input"
                />
              </div>

              <div className="form-group">
                <label>Tags</label>
                <div className="tags-editor">
                  <div className="current-tags">
                    {formData.tags.map(tag => (
                      <span key={tag} className="tag-chip">
                        {tag}
                        <button type="button" onClick={() => handleRemoveTag(tag)}>×</button>
                      </span>
                    ))}
                  </div>
                  <div className="tag-input-row">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="Add a tag"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddTag(e)}
                    />
                    <button type="button" onClick={handleAddTag} className="btn-add-tag">
                      Add
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <label className="publish-toggle">
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({...formData, published: e.target.checked})}
                  />
                  <span>Publish immediately</span>
                </label>
                <button type="submit" className="btn-save" data-testid="save-post-btn">
                  <Save size={18} />
                  {editingPost ? 'Update Post' : 'Create Post'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          /* Posts List */
          <div className="posts-list">
            {loading ? (
              <div className="loading">Loading posts...</div>
            ) : posts.length === 0 ? (
              <div className="empty">
                <p>No blog posts yet.</p>
                <button onClick={() => setIsCreating(true)} className="btn-create">
                  Create your first post
                </button>
              </div>
            ) : (
              <table className="posts-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Author</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map(post => (
                    <tr key={post.id} data-testid={`post-row-${post.slug}`}>
                      <td className="title-cell">
                        <strong>{post.title}</strong>
                        <span className="slug">/{post.slug}</span>
                      </td>
                      <td>{post.author}</td>
                      <td>{formatDate(post.created_at)}</td>
                      <td>
                        <span className={`status ${post.published ? 'published' : 'draft'}`}>
                          {post.published ? (
                            <><Eye size={14} /> Published</>
                          ) : (
                            <><EyeOff size={14} /> Draft</>
                          )}
                        </span>
                      </td>
                      <td className="actions-cell">
                        {post.published && (
                          <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="btn-view">
                            <Eye size={16} />
                          </a>
                        )}
                        <button onClick={() => handleEdit(post)} className="btn-edit" data-testid={`edit-post-${post.slug}`}>
                          <Edit size={16} />
                        </button>
                        <button onClick={() => handleDelete(post.id)} className="btn-delete" data-testid={`delete-post-${post.slug}`}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default BlogAdmin;
